import { InvalidFileInfo } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/types.ts';
import { fx } from '@fxts/core';
import { html, UnsafeHtml } from 'rune-ts';
import klass from './ProductThumbnailForm.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import {
  FileUploadError,
  FileUploadHelper,
} from '@services/MarppleShop/shared/components/cells/FileUpload/FileUploadHelper.ts';
import { FileUploadErrorJsonCode } from '@services/MarppleShop/shared/components/cells/FileUpload/FileExtensions.ts';

// 유효하지 않은 파일에 대한 오류 메시지 생성
export function generateSingleInvalidFileTitle(invalidFile: InvalidFileInfo): string {
  const { type } = invalidFile;

  switch (type) {
    case FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED:
    case FileUploadErrorJsonCode.EXTENSION_FORBIDDEN: {
      return `파일 첨부 불가능`;
    }
    case FileUploadErrorJsonCode.FILE_SIZE_OVER: {
      return `업로드 용량 초과`;
    }
    case FileUploadErrorJsonCode.MIN_WIDTH:
    case FileUploadErrorJsonCode.MIN_HEIGHT: {
      return `이미지 사이즈 미달`;
    }
    case FileUploadErrorJsonCode.MAX_WIDTH:
    case FileUploadErrorJsonCode.MAX_HEIGHT: {
      return `이미지 사이즈 초과`;
    }
    case FileUploadErrorJsonCode.READ_IMAGE_FAILED:
      return `업로드 오류`;
    case FileUploadErrorJsonCode.UNEXPECTED:
    default: {
      return `알 수 없는 오류`;
    }
  }
}

// 유효하지 않은 파일에 대한 오류 메시지 생성
function generateSingleInvalidFileMessage(invalidFile: InvalidFileInfo, validator: FileUploadHelper): string {
  const { type } = invalidFile;

  switch (type) {
    case FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED:
    case FileUploadErrorJsonCode.EXTENSION_FORBIDDEN: {
      return `선택한 파일은 첨부가 불가능한 파일입니다.\n확장자를 다시 확인해 주세요.`;
    }
    case FileUploadErrorJsonCode.FILE_SIZE_OVER: {
      return `업로드 가능한 용량을 초과하였습니다.\n${validator.options.max_file_size}MB 이하의 파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.MIN_WIDTH:
    case FileUploadErrorJsonCode.MIN_HEIGHT: {
      return `업로드 가능한 사이즈보다 작습니다.\n최소 ${validator.options.min_width}px 이상의 파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.MAX_WIDTH:
    case FileUploadErrorJsonCode.MAX_HEIGHT: {
      return `업로드 가능한 사이즈보다 큽니다.\n최대 ${validator.options.max_width}px 이하의 파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.READ_IMAGE_FAILED: {
      return `이미지를 읽는 중 오류가 발생했습니다.`;
    }
    case FileUploadErrorJsonCode.UNEXPECTED:
    default: {
      return `이미지 검증에 실패했습니다.`;
    }
  }
}

// 상세 설명에서 유효하지 않은 파일에 대한 오류 메시지 생성
function generateDetailSingleInvalidFileMessage(
  invalidFile: InvalidFileInfo,
  validator: FileUploadHelper,
): string {
  const { type } = invalidFile;

  switch (type) {
    case FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED:
    case FileUploadErrorJsonCode.EXTENSION_FORBIDDEN: {
      return `선택한 파일은 첨부가 불가능한 파일입니다.\n확장자를 다시 확인해 주세요.`;
    }
    case FileUploadErrorJsonCode.FILE_SIZE_OVER: {
      return `업로드 가능한 용량을 초과하였습니다.\n${validator.options.max_file_size}MB 이하의 파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.MIN_WIDTH:
    case FileUploadErrorJsonCode.MIN_HEIGHT: {
      return `업로드 가능한 사이즈보다 작습니다.\n최소 ${validator.options.min_width}px 이상의 파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.MAX_WIDTH:
    case FileUploadErrorJsonCode.MAX_HEIGHT: {
      return `업로드 가능한 사이즈보다 큽니다.\n가로 ${validator.options.max_width}px 이하, 세로 ${validator.options.max_height}px 이하의\n파일로 업로드해 주세요.`;
    }
    case FileUploadErrorJsonCode.READ_IMAGE_FAILED: {
      return `이미지를 읽는 중 오류가 발생했습니다.`;
    }
    case FileUploadErrorJsonCode.UNEXPECTED:
    default: {
      return `이미지 검증에 실패했습니다.`;
    }
  }
}

// 유효하지 않은 파일에 대한 오류 메시지 생성
function generateMultipleInvalidFileMessage(
  invalidFile: InvalidFileInfo,
  validator: FileUploadHelper,
): string {
  const { file, type } = invalidFile;

  switch (type) {
    case FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED:
    case FileUploadErrorJsonCode.EXTENSION_FORBIDDEN: {
      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>지원 불가 형식</p><span>JPG, PNG 가능</span></div></div>`;
    }
    case FileUploadErrorJsonCode.FILE_SIZE_OVER: {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>용량 초과 (현재: ${fileSizeMB}MB)</p><span>최대 ${validator.options.max_file_size}MB</span></div></div>`;
    }
    case FileUploadErrorJsonCode.MIN_WIDTH:
    case FileUploadErrorJsonCode.MIN_HEIGHT: {
      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>사이즈 미달</p><span>최소 ${validator.options.min_width}px ~ 최대 ${validator.options.max_width}px</span></div></div>`;
    }
    case FileUploadErrorJsonCode.MAX_WIDTH:
    case FileUploadErrorJsonCode.MAX_HEIGHT: {
      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>사이즈 초과</p><span>최소 ${validator.options.min_width}px ~ 최대 ${validator.options.max_width}px</span></div></div>`;
    }
    case FileUploadErrorJsonCode.READ_IMAGE_FAILED: {
      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>업로드 오류</p><span>이미지를 읽는 중 오류가 발생했습니다.</span></div></div>`;
    }
    case FileUploadErrorJsonCode.UNEXPECTED:
    default: {
      return `<div class="${klass.alert_message}"><b class="${shareFont('14_bold')}">${file.name}</b><div class="${shareFont('14_medium')}"><p>알 수 없는 오류</p><span>이미지 검증에 실패했습니다.</span></div></div>`;
    }
  }
}

// 이미지 목록에 대한 병렬 검증 수행
export async function validateImages(
  files: File[],
  fail_images: InvalidFileInfo[],
  validator: FileUploadHelper,
): Promise<File[]> {
  // 이미지 가로/세로 크기 검증
  const validationResults = await fx(files)
    .toAsync()
    .map((file) =>
      validator.validate(file).catch((error: unknown) => {
        if (error instanceof FileUploadError) {
          const { code } = error.json;
          switch (code) {
            case FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED:
              fail_images.push({ file, type: FileUploadErrorJsonCode.EXTENSION_NOT_ALLOWED });
              break;
            case FileUploadErrorJsonCode.EXTENSION_FORBIDDEN:
              fail_images.push({ file, type: FileUploadErrorJsonCode.EXTENSION_FORBIDDEN });
              break;
            case FileUploadErrorJsonCode.MIME_TYPE_NOT_ALLOWED:
              fail_images.push({ file, type: FileUploadErrorJsonCode.MIME_TYPE_NOT_ALLOWED });
              break;
            case FileUploadErrorJsonCode.FILE_SIZE_OVER:
              fail_images.push({ file, type: FileUploadErrorJsonCode.FILE_SIZE_OVER });
              break;
            case FileUploadErrorJsonCode.READ_IMAGE_FAILED:
              fail_images.push({ file, type: FileUploadErrorJsonCode.READ_IMAGE_FAILED });
              break;
            case FileUploadErrorJsonCode.MAX_WIDTH:
              fail_images.push({ file, type: FileUploadErrorJsonCode.MAX_WIDTH });
              break;
            case FileUploadErrorJsonCode.MAX_HEIGHT:
              fail_images.push({ file, type: FileUploadErrorJsonCode.MAX_HEIGHT });
              break;
            case FileUploadErrorJsonCode.MIN_WIDTH:
              fail_images.push({ file, type: FileUploadErrorJsonCode.MIN_WIDTH });
              break;
            case FileUploadErrorJsonCode.MIN_HEIGHT:
              fail_images.push({ file, type: FileUploadErrorJsonCode.MIN_HEIGHT });
              break;
            default:
              fail_images.push({ file, type: FileUploadErrorJsonCode.UNEXPECTED });
          }
        }
        return { isValid: false, file };
      }),
    )
    .concurrent(10) // 동시에 10개의 이미지를 처리
    .toArray();

  // 유효한 파일만 필터링하여 반환
  return validationResults.filter((result) => result.isValid).map((result) => result.file);
}

export function createAlertSingleMessage(
  fail_images: InvalidFileInfo[],
  validator: FileUploadHelper,
): {
  title: string;
  message: string | UnsafeHtml;
} {
  const invalidFileTitle = fail_images.map(generateSingleInvalidFileTitle).join('');
  const invalidFileMessage = fail_images
    .map((invalidFileInfo: InvalidFileInfo) => generateSingleInvalidFileMessage(invalidFileInfo, validator))
    .join('');

  return {
    title: invalidFileTitle,
    message: invalidFileMessage,
  };
}

export function createAlertDetailSingleMessage(
  fail_images: InvalidFileInfo[],
  validator: FileUploadHelper,
): {
  title: string;
  message: string | UnsafeHtml;
} {
  const invalidFileTitle = fail_images.map(generateSingleInvalidFileTitle).join('');
  const invalidFileMessage = fail_images
    .map((invalidFileInfo: InvalidFileInfo) =>
      generateDetailSingleInvalidFileMessage(invalidFileInfo, validator),
    )
    .join('');

  return {
    title: invalidFileTitle,
    message: invalidFileMessage,
  };
}

export function createAlertMultipleMessage(
  fail_images: InvalidFileInfo[],
  validator: FileUploadHelper,
): {
  title: string;
  message: string | UnsafeHtml;
} {
  const invalidFileMessages = fail_images.map((invalidFileInfo: InvalidFileInfo) =>
    generateMultipleInvalidFileMessage(invalidFileInfo, validator),
  );
  const alertMessage =
    '선택한 이미지 중 일부를 업로드할 수 없습니다.\n아래의 사유를 확인해 주세요.' +
    `<div class="${klass.multiple_alert_message}">${invalidFileMessages.join('')}</div>`;

  return {
    title: '업로드 불가 안내',
    message: html.preventEscape(alertMessage),
  };
}
